/**
 * Plain labels for the API's codes, in the glossary of docs/workspace.md. The API sends a
 * label next to most codes; these cover the ones a filter or a tag needs before a row exists.
 */
import type { HouseStatus, LetterTemplateKey, NextTouchFilter, SourceTag, Stage, StageFilter } from "@/api/models";

export const STAGE_LABELS: Record<Stage, string> = {
  waiting_for_court: "Waiting for court",
  executor_approved: "Executor approved",
  heirs_can_sign: "Heirs can sign",
  order_returned_unsigned: "Order returned unsigned",
  closed: "Closed",
  not_stated: "Not stated",
};

export const STAGE_FILTERS: { value: StageFilter; label: string }[] = [
  ...(Object.entries(STAGE_LABELS) as [Stage, string][]).map(([value, label]) => ({ value, label })),
  { value: "dropped", label: "Dropped" },
];

/** How a stage pill is coloured: approved and heirs-can-sign are good news, unsigned needs a look. */
export type Tone = "ok" | "wait" | "warn" | "muted";
export const STAGE_TONES: Record<Stage, Tone> = {
  waiting_for_court: "wait",
  executor_approved: "ok",
  heirs_can_sign: "ok",
  order_returned_unsigned: "warn",
  closed: "muted",
  not_stated: "muted",
};

export const SOURCE_TAG_LABELS: Record<SourceTag, string> = {
  court: "Court",
  tax_roll: "Tax roll",
  document: "Document",
  sample: "Sample",
  skip_trace: "Skip tracing",
  state_bar: "State Bar",
  firm_website: "Firm website",
  none: "Not found",
};

export const NEXT_TOUCH_FILTERS: { value: NextTouchFilter; label: string }[] = [
  { value: "all", label: "Any time" },
  { value: "due", label: "Due now" },
  { value: "week", label: "Due this week" },
];

/** What the agent calls each letter; the API picks which one a family gets. */
export const LETTER_NAMES: Record<LetterTemplateKey, string> = {
  first_contact: "First letter",
  resources: "Help with the house letter",
  home_value: "Home value letter",
  follow_up: "Check-in letter",
  executor_approved: "Executor approved letter",
};

/** The founder's words for whether the house is still in the deceased's name. */
export const HOUSE_STATUS_LABELS: Record<HouseStatus, string> = {
  available: "Still in the deceased's name",
  public_record: "Ownership corroborated by public records",
  lived_here: "The deceased's home; owner not public",
  listed: "Listed for sale",
  sold: "Sold or transferred",
  similar_name: "Similar name on the roll",
  name_unconfirmed: "Their name on the roll, not confirmed",
  family_name: "Family name on the roll",
  trust: "Held in a trust",
  unknown: "Unknown",
};

export const HOMESTEAD_LABELS = { yes: "yes", no: "no", not_checked: "not checked" } as const;
